// The Cross linked courses are brought into the codeOptions to be sorted in the Departmental Code filter 
// The Cross linked course display starts at line 566 - 585: {program.crossListedCourseCode && 


import React, { useState, useEffect } from "react";
import Select from "react-select";
import Seo from "../components/seo";
import { graphql, useStaticQuery } from "gatsby";
import { Container } from "react-bootstrap";
import "../styles/index-styles.css";
import "../styles/course-finder-styles.css";
import { Button } from "@contentful/f36-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faCircleExclamation,
  faCircleInfo,
  faChevronDown,
  faChevronUp,
  faFilter,
  faCircleDot,
  faShareFromSquare,
  faArrowRight,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import moment from "moment";
import subjectMapping from "./subjectMapping";

const UnderGradCourseFilter = () => {
  const data = useStaticQuery(graphql`
    query {
      allContentfulSuCourse {
        nodes {
          courseTitle
          courseImage {
            url
          }
          courseDescription {
            raw
          }
          courseType
          courseSlug
          contentful_id
          id
          startOfSession
          remoteOrCampus
          courseStartDate
          courseEndDate
          classDays
          classStartTime
          classEndTime
          academicInterest
          duration
          currentGrade
          courseCode
          crossListedCourseCode
          coreCourse
          courseFull
        }
      }
    }
  `);
  const courses = data?.allContentfulSuCourse?.nodes || [];

  const uniqueValues = (key) =>
    [...new Set(courses.map((course) => course[key]))].filter(Boolean);

  const reversedSubjectMapping = Object.fromEntries(
    Object.entries(subjectMapping).map(([code, subject]) => [subject, code])
  );

  const sortedPrograms = courses.sort((a, b) => {
    return a.courseTitle.localeCompare(b.courseTitle);
  });

  const [expandedRows, setExpandedRows] = useState({});

  const toggleRow = (id) => {
    setExpandedRows((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };


  // **************************************** Academic Interest Options ****************************************
  const interestOptions = [
    {
      value: "Select All",
      label: "Select All",
    },
    ...[
      ...new Set(
        courses
          .filter((course) => course.courseType !== "Pre-College")
          .map((course) =>
            Array.isArray(course.academicInterest)
              ? course.academicInterest
              : [course.academicInterest]
          )
          .flat()
          .filter(Boolean)
      ),
    ]
      .sort((a, b) => {
        const strA = String(a);
        const strB = String(b);
        return strA.localeCompare(strB);
      })
      .map((interest) => ({
        value: interest,
        label: interest,
      })),
  ];

  // **************************************** FORMAT DATE ****************************************
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return moment(dateString).format("MMM DD, YYYY");
  };

  const dateRangeOptions = [
    ...new Set(
      courses
        .filter((course) => course.courseType !== "Pre-College")
        .map((course) => {
          if (course.courseStartDate && course.courseEndDate) {
            return `${course.courseStartDate} - ${course.courseEndDate}`;
          }
          return null;
        })
        .filter(Boolean) // Ensure no null values
    ),
  ]
    .sort((a, b) => new Date(a.split(" - ")[0]) - new Date(b.split(" - ")[0]))
    .map((dateRange) => ({
      value: dateRange,
      label: `${formatDate(dateRange.split(" - ")[0])} - ${formatDate(
        dateRange.split(" - ")[1]
      )}`,
    }));

  // ************************************** COURSE / DEPARTMENTAL CODE OPTIONS **************************************
  const codeOptions = [
    ...new Set(
      courses
        .filter((course) => course.courseType !== "Pre-College")
        .flatMap((course) => {
          const crossListedCodes = Array.isArray(course.crossListedCourseCode)
            ? course.crossListedCourseCode.filter(Boolean).map((code) => code.trim().slice(0, 4))
            : course.crossListedCourseCode
              ? course.crossListedCourseCode.split(',').map(code => code.trim().slice(0, 4))
              : [];

          const mainCodes = Array.isArray(course.courseCode)
            ? course.courseCode.filter(Boolean).map((code) => code.slice(0, 4))
            : course.courseCode
              ? [course.courseCode.slice(0, 4)]
              : [];

          return [...crossListedCodes, ...mainCodes];
        })
        .filter(Boolean) // Ensure no null or undefined values
    )
  ]
    .sort((a, b) => a.localeCompare(b))
    .map((codePrefix) => ({
      value: codePrefix,
      label: `${codePrefix} - ${subjectMapping[codePrefix] || "Unknown"}`,
    }));

  // ************************************** AM I CORE OR NOT OPTIONS **************************************
  const coreOptions = [
    { value: "all", label: "All Courses" },
    { value: true, label: "Core Course" },
    { value: false, label: "Non Core Course" },
  ];

  // ************************************** MODALITY OPTIONS **************************************
  const userType = "undergraduate";

  const formatOptions = uniqueValues("remoteOrCampus")
    .sort((a, b) => a.localeCompare(b))
    .map((format) => ({
      value: format,
      label:
        userType === "undergraduate" && format === "Residential"
          ? "In-Person"
          : format,
    }));

  // ************************************** 24 to 12 Hour FORMAT **************************************

  const convertTo12HourFormat = (time) => {
    if (!time) return "";
    const [hour, minute] = time.split(":").map(Number);
    const period = hour >= 12 ? "PM" : "AM";
    const adjustedHour = hour % 12 || 12;
    return `${adjustedHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const [selectedDurations, setSelectedDurations] = useState([]);
  const [selectedClassdays, setSelectedClassdays] = useState([]);
  const [selectedClassTimeframes, setSelectedClassTimeframes] = useState([]);
  const [selectedTimeframes, setSelectedTimeframes] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [selectedFormats, setSelectedFormats] = useState([]);
  const [selectedDateRanges, setSelectedDateRanges] = useState([]);
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [selectedCore, setSelectedCore] = useState(null);
  const [isFilterActive, setIsFilterActive] = useState(false);

  const [filteredDepartmentCode, setFilteredDepartmentCode] = useState("");

  useEffect(() => {
    const hasActiveFilter =
      selectedDurations.length > 0 ||
      selectedClassTimeframes.length > 0 ||
      selectedTimeframes.length > 0 ||
      selectedClassdays.length > 0 ||
      selectedInterests.length > 0 ||
      selectedDateRanges.length > 0 ||
      selectedFormats.length > 0 ||
      selectedCodes.length > 0 ||
      selectedCore !== null;

    setIsFilterActive(hasActiveFilter);
  }, [
    selectedDurations,
    selectedTimeframes,
    selectedInterests,
    selectedFormats,
    selectedClassTimeframes,
    selectedClassdays,
    selectedCore,
    selectedCodes,
    selectedDateRanges,
  ]);

  const resetFilters = () => {
    setSelectedDurations([]);
    setSelectedTimeframes([]);
    setSelectedInterests([]);
    setSelectedFormats([]);
    setSelectedCore(null);
    setSelectedClassTimeframes([]);
    setSelectedClassdays([]);
    setSelectedCodes([]);
    setSelectedDateRanges([]);

    const allInputs = document.querySelectorAll("input");
    allInputs.forEach((input) => {
      input.checked = false;
      input.blur();
    });
  };

  const matchesDateRange = (program, selectedDateRanges) => {
    if (selectedDateRanges.length === 0) return true;
    const programRange = `${program.courseStartDate} - ${program.courseEndDate}`;
    return selectedDateRanges.some(({ value }) => value === programRange);
  };

  const formatCrossListedCodes = (crossListedCodes) => {
    if (!crossListedCodes) return [];
    if (typeof crossListedCodes === "string") {
      return crossListedCodes.split(",").map((code) => code.trim());
    }
    // If it's already an array, return it
    return Array.isArray(crossListedCodes)
      ? crossListedCodes
      : [crossListedCodes];
  };

  const matchesCode = (program, selectedCodes) => {
    if (selectedCodes.length === 0) return true;

    const mainCodePrefix = Array.isArray(program.courseCode)
      ? program.courseCode
        .filter((code) => code)
        .map((code) => code.slice(0, 4))
      : program.courseCode
        ? [program.courseCode.slice(0, 4)]
        : [];

    // Get cross-listed course code prefixes
    const crossListedPrefixes = program.crossListedCourseCode
      ? formatCrossListedCodes(program.crossListedCourseCode).map((code) =>
        code.slice(0, 4)
      )
      : [];

    // Combine all relevant course code prefixes
    const allCodePrefixes = [...mainCodePrefix, ...crossListedPrefixes];

    // Check if any of the selected codes match any of the course prefixes
    return selectedCodes.some((selected) =>
      allCodePrefixes.includes(selected.value)
    );
  };

  const filteredPrograms = isFilterActive
    ? courses.filter((program) => {
      if (program.courseType === "Pre-College") {
        return false;
      }

      const matchesDuration =
        selectedDurations.length === 0 ||
        selectedDurations.some((d) => d.value === program.duration);

      const matchesInterest =
        selectedInterests.length === 0 ||
        selectedInterests.some((si) =>
          Array.isArray(program.academicInterest)
            ? program.academicInterest.includes(si.value)
            : program.academicInterest === si.value
        );

      const codeMatches = matchesCode(program, selectedCodes);

      const matchesFormat =
        selectedFormats.length === 0 ||
        selectedFormats.some((sf) => sf.value === program.remoteOrCampus);

      const matchesClassTime =
        selectedClassTimeframes.length === 0 ||
        selectedClassTimeframes.some(
          (ct) => ct.value === program.classStartTime
        );

      const matchesClassDays =
        selectedClassdays.length === 0 ||
        selectedClassdays.some((cd) =>
          Array.isArray(program.classDays)
            ? program.classDays.includes(cd.value)
            : program.classDays === cd.value
        );

      const matchesCore =
        selectedCore === null ||
        selectedCore.value === "all" ||
        selectedCore.value === program.coreCourse;

      const matchesDate = matchesDateRange(program, selectedDateRanges);

      const doesNotStartWithZZZ = !program.courseTitle.startsWith(
        "ZZZ Placeholder"
      );

      return (
        matchesDuration &&
        matchesInterest &&
        codeMatches &&
        matchesFormat &&
        matchesClassTime &&
        matchesClassDays &&
        matchesCore &&
        matchesDate &&
        doesNotStartWithZZZ
      );
    })
    : [];

  // console.log("Available departmental codes:", codeOptions.map(option => option.value));
  const handleMultiSelectChange = (selectedOptions, setSelected) => {
    if (setSelected === setSelectedCodes) {
      const lastSelected = selectedOptions?.[selectedOptions.length - 1];
      if (lastSelected) {
        setFilteredDepartmentCode(lastSelected.value);
      } else {
        setFilteredDepartmentCode("");
      }
    }

    if (selectedOptions?.some((item) => item.value === "Select All")) {
      if (selectedOptions.length === interestOptions.length) {
        setSelected([]);
      } else {
        setSelected(
          interestOptions.filter((option) => option.value !== "Select All")
        );
      }
    } else {
      setSelected(selectedOptions || []);
    }
  };

  const handleSingleSelectChange = (selectedOption, setSelected) => {
    setSelected(selectedOption);
  };

  return (
    <Container className="undergrad-filter-box">
      <div key={courses.id} id={courses.styleId}>
        <button class="button" onClick={resetFilters}>
          <svg class="svgIcon" viewBox="0 0 384 512">
            <FontAwesomeIcon icon={faFilter} />
          </svg>
        </button>
      </div>

      {/* ************************************* START OF THE DROPDOWNS ************************************* */}
      <div className="dropdown-container">
        <Select
          options={interestOptions}
          isMulti
          placeholder="Area of Interest"
          value={selectedInterests}
          onChange={(selected) =>
            handleMultiSelectChange(selected, setSelectedInterests)
          }
          className="dropdown-select"
        />

        <Select
          options={codeOptions}
          isMulti
          placeholder="Departmental Code"
          value={selectedCodes}
          onChange={(selected) =>
            handleMultiSelectChange(selected, setSelectedCodes)
          }
          className="dropdown-select"
        />

        <Select
          options={dateRangeOptions}
          isMulti
          placeholder="Dates"
          value={selectedDateRanges}
          onChange={(selected) =>
            handleMultiSelectChange(selected, setSelectedDateRanges)
          }
          className="dropdown-select"
        />

        <Select
          options={formatOptions}
          isMulti
          placeholder="Modality"
          value={selectedFormats}
          onChange={(selected) =>
            handleMultiSelectChange(selected, setSelectedFormats)
          }
          className="dropdown-select"
        />

        <Select
          options={coreOptions}
          isMulti={false}
          placeholder="Part of Core?"
          value={selectedCore}
          onChange={(selected) =>
            handleSingleSelectChange(selected, setSelectedCore)
          }
          className="dropdown-select"
        />
      </div>

      {/* ************************************* START OF THE DISPLAY RESULTS ************************************* */}
      <Container>
        {isFilterActive && filteredPrograms.length > 0 ? (
          <div className="grad-filtered-programs-container">
            <table className="grad-filtered-programs-table">
              <thead>
                <tr className="grad-filtered-programs-header">
                  <th className="grad-course-header">Course Code</th>
                  <th className="grad-title-header">Course Title</th>
                  <th className="grad-classsession-header">Session</th>
                  <th className="grad-core-header">Core?</th>
                  <th></th>
                </tr>
              </thead>


              {/* ************************************* THIS WAY IS THE NEW WAY WHICH WILL SHOW ALL CODES & CROSS LINKED CODES THAT HAVE A MATCHING CODE ************************************* */}
              <tbody className="grad-filtered-programs-list">
                {filteredPrograms
                  .sort((a, b) => {
                    if (!a.courseCode || !b.courseCode) return 0;
                    return a.courseCode.localeCompare(b.courseCode);
                  })
                  .map((program, index) => (
                    <React.Fragment key={program.id}>




                      {/*                       
                      <tr className="grad-filtered-programs-item"> */}
                      {/* Course Code Column */}
                      {/* <td className="grad-course-code">
                          <div className="primary-code">
                            {program.courseCode}
                          </div>
                        </td> */}




                      <tr className="grad-filtered-programs-item">
                        {/* Course Code Column */}
                        <td className={`grad-course-code ${program.courseFull === "Course FULL" ? "course-full-code" : ""}`}>
                          <div className="primary-code">
                            {program.courseCode}
                          </div>
                        </td>





                        {/* ************************Course Title Column************************ */}
                        {/* <td className="grad-course-title">
                          <a href={`/courses/${program.courseSlug}`}>
                            {program.courseTitle}
                          </a> */}



                        {/* ************************Course Title Column************************ */}
                        <td className={`grad-course-title ${program.courseFull === "Course FULL" ? "course-full-title" : ""}`}>
                          <a href={`/courses/${program.courseSlug}`}>
                            {program.courseTitle}
                          </a>






                          {/* ************************Cross-Listed Courses Section************************ */}
                          {program.crossListedCourseCode && (
                            <div className="cross-listed-label">
                              <div className="tooltip-container">
                                <FontAwesomeIcon icon={faCircleInfo} className="info-icon" />
                                <span className="tooltip-text">Cross-Listed Courses</span>
                              </div>



                              {/* ************************Format Cross-Listed Codes************************ */}
                              {/* {formatCrossListedCodes(program.crossListedCourseCode).map((code, index) => (
                                <React.Fragment key={index}>
                                  {index > 0 && ", "}
                                  <span
                                    className={
                                      selectedCodes.some((selected) =>
                                        code.startsWith(selected.value.substring(0, 4))
                                      )
                                        ? "highlight-matching-code"
                                        : ""
                                    }
                                  >
                                    {code.replace(" ", "\u00A0")}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          )} */}






                              {formatCrossListedCodes(program.crossListedCourseCode).map((code, index) => (
                                <React.Fragment key={index}>
                                  {index > 0 && ", "}
                                  <span
                                    className={`${selectedCodes.some((selected) =>
                                      code.startsWith(selected.value.substring(0, 4))
                                    )
                                      ? "highlight-matching-code"
                                      : ""
                                      } ${program.courseFull === "Course FULL" ? "course-full-code" : ""}`}
                                  >
                                    {code.replace(" ", "\u00A0")}
                                  </span>
                                </React.Fragment>
                              ))}
                            </div>
                          )}







                          {/* ************************ COURSE FULL ************************ */}

                          {program.courseFull === "Course FULL" && (
                            <div className="cross-listed-label-full">
                              <div className="tooltip-container">
                                <p>COURSE FULL</p>
                              </div>
                            </div>
                          )}










                        </td>



























                        {/* ************************Class Session Column************************ */}
                        {/* <td className="grad-class-session">
                          {program.startOfSession}
                        </td> */}

                        {/* ************************Core Course Column************************ */}
                        {/* <td className="grad-core-session">
                          {program.coreCourse === true ? (
                            <FontAwesomeIcon icon={faCircleDot} />
                          ) : program.coreCourse === false ? (
                            <FontAwesomeIcon icon={faBan} />
                          ) : (
                            "N/A"
                          )}
                        </td> */}







                        {/* ************************Class Session Column************************ */}
                        <td className={`grad-class-session ${program.courseFull === "Course FULL" ? "course-full-column" : ""}`}>
                          {program.startOfSession}
                        </td>

                        {/* ************************Core Course Column************************ */}
                        <td className={`grad-core-session ${program.courseFull === "Course FULL" ? "course-full-column" : ""}`}>
                          {program.coreCourse === true ? (
                            <FontAwesomeIcon icon={faCircleDot} />
                          ) : program.coreCourse === false ? (
                            <FontAwesomeIcon icon={faBan} />
                          ) : (
                            "N/A"
                          )}
                        </td>





                        {/* ************************Accordion Toggle Button************************ */}
                        <td>
                          <button
                            className="accordion-toggle"
                            onClick={() => toggleRow(program.id)}
                          >
                            <FontAwesomeIcon
                              icon={
                                expandedRows[program.id]
                                  ? faChevronUp
                                  : faChevronDown
                              }
                            />
                          </button>
                        </td>
                      </tr>
                      <tr
                        className={`accordion-content ${expandedRows[program.id] ? "open" : ""
                          }`}
                      >
                        <td className="accordion-details" colSpan="5">
                          <td className="inline-titles">
                            <div className="inline-content">
                              <h4>Dates:</h4>
                              <span>
                                {formatDate(program.courseStartDate)} -{" "}
                                {formatDate(program.courseEndDate)}
                              </span>
                            </div>
                          </td>
                          <td className="inline-titles">
                            <div className="inline-content">
                              <h4>Days:</h4>
                              <span>
                                {program.classDays
                                  ? program.classDays
                                    .sort((a, b) => {
                                      const dayOrder = [
                                        "Monday",
                                        "Tuesday",
                                        "Wednesday",
                                        "Thursday",
                                        "Friday",
                                        "Saturday",
                                      ];
                                      return (
                                        dayOrder.indexOf(a) -
                                        dayOrder.indexOf(b)
                                      );
                                    })
                                    .map((day) =>
                                      day.slice(0, 2).toUpperCase()
                                    )
                                    .join(", ")
                                  : "N/A"}
                              </span>
                            </div>
                          </td>

                          <td className="inline-titles">
                            <div className="inline-content">
                              <h4>Time:</h4>
                              <span>
                                <td className="grad-class-starttime">
                                  {program.classStartTime
                                    ? convertTo12HourFormat(
                                      program.classStartTime
                                    )
                                    : "Standard Class Hours"}{" "}
                                  -
                                </td>
                                <td className="grad-class-endtime">
                                  {program.classEndTime
                                    ? convertTo12HourFormat(
                                      program.classEndTime
                                    )
                                    : "Standard Class Hours"}
                                </td>
                              </span>
                            </div>
                          </td>

                          {program.crossListedCourseCode ? (
                            <>
                              <div className="crosslisted-course">
                                <h4 class="inline-text">
                                  Cross Listed Course Code(s):
                                </h4>
                                <span class="inline-text">
                                  {program.crossListedCourseCode}
                                </span>
                              </div>
                            </>
                          ) : null}
                          <p className="results-description">
                            {renderRichText(program.courseDescription) ||
                              "No description available"}
                          </p>
                          <div className="accordion-view-course">
                            <a
                              href={`/courses/${program.courseSlug}`}
                              className="view-course-link"
                            >
                              View Course
                            </a>
                          </div>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
              </tbody>
            </table>
          </div>
        ) : isFilterActive ? (
          <h3>No courses found for the selected filters at this time!</h3>
        ) : null}
      </Container>
    </Container>
  );
};

export const Head = () => <Seo title="Course Finder" />;

export default UnderGradCourseFilter;